import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { Popper } from 'radix-ui/internal';
import type { ComponentProps, ReactElement } from 'react';

import { cn } from '@/lib/utils';
import { TooltipEventKind } from '@/types/tooltip';

import { useTooltipContext } from './tooltipContext';

// Our tooltips mount Radix parts of their own, with a virtual anchor that Radix's tooltip does
// not export. A scope of our own lets Popper's anchor reach the Popper of that Radix tooltip.
const useTooltipScope = TooltipPrimitive.createTooltipScope();

// A tooltip never takes the pointer: one that covers its trigger would take the hover from it,
// and close and open in a loop. The Radix wrapper around the content is a box of its own, so
// `[:has(>&)]` puts `pointer-events: none` on the wrapper, and the content inherits it.
export const TooltipContent = ({
    className,
    sideOffset = 0,
    children,
    ...props
}: ComponentProps<typeof TooltipPrimitive.Content>): ReactElement | null => {
    const { state, contentId, triggerRef, send } = useTooltipContext('TooltipContent');
    const scope = useTooltipScope(undefined);
    if (!state.isShown) {
        return null;
    }
    const popperScope = { __scopePopper: scope.__scopeTooltip };
    return (
        <TooltipPrimitive.Provider
            {...scope}
            disableHoverableContent
        >
            <TooltipPrimitive.Root
                {...scope}
                open={state.isOpen}
                onOpenChange={(isOpen): void => {
                    if (!isOpen) {
                        send({ kind: TooltipEventKind.OutsideClose });
                    }
                }}
            >
                <Popper.Anchor
                    {...popperScope}
                    virtualRef={triggerRef}
                />
                <TooltipPrimitive.Portal {...scope}>
                    <TooltipPrimitive.Content
                        {...scope}
                        ref={(node): void => {
                            if (node === null) {
                                send({ kind: TooltipEventKind.ContentRemoved });
                            }
                        }}
                        id={contentId}
                        data-slot="tooltip-content"
                        sideOffset={sideOffset}
                        className={cn(
                            '[:has(>&)]:pointer-events-none',
                            `z-50 w-fit origin-(--radix-tooltip-content-transform-origin)
                            animate-in rounded-md bg-occ-background-inverted px-3 py-1.5 text-xs
                            text-balance
                            text-occ-foreground-inverted fade-in-0 zoom-in-95
                            data-[side=bottom]:slide-in-from-top-2
                            data-[side=left]:slide-in-from-right-2
                            data-[side=right]:slide-in-from-left-2
                            data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out
                            data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95
                            [&>span]:overflow-hidden`,
                            className,
                        )}
                        {...props}
                    >
                        {children}
                        <TooltipPrimitive.Arrow
                            {...scope}
                            className={`relative -z-10 size-2.5 translate-y-[calc(-50%_-_2px)]
                                rotate-45 rounded-[2px] bg-occ-background-inverted
                                fill-occ-background-inverted`}
                        />
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
};
